import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { useLogin } from "../hooks/useLogin";
import { GlobeLocaleDropdown } from "../components/GlobeLocaleDropdown";
import { Banner } from "../components/Banner";
import { Button } from "../components/Button";
import { TextField } from "../components/TextField";

/**
 * PM sign-in screen (PMAUTH-8.1).
 * Centered card on bg-surface, globe dropdown above.
 * On success → navigate to /; on error → inline ERR_AUTH_FAILED or ERR_CLIENT_CLOCK_SKEW.
 */
export function Login() {
  const intl = useIntl();
  const navigate = useNavigate();
  const login = useLogin();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const errorCode = login.error?.code;
  const isClockSkew = errorCode === "ERR_CLIENT_CLOCK_SKEW";
  const isAuthFailed = errorCode === "ERR_AUTH_FAILED";
  // 2026-05-02 walkthrough P2 — any other error code (network, 422
  // ERR_VALIDATION, server 5xx) was previously a silent no-op because
  // only the two codes above flipped a banner. Catch everything else
  // here so the worker sees something on screen instead of a dead form.
  const isOtherError = !!errorCode && !isClockSkew && !isAuthFailed;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate(
      { username, password },
      {
        onSuccess: () => navigate("/", { replace: true }),
      },
    );
  };

  // Clear error when user types
  const handleUsernameChange = (v: string) => {
    setUsername(v);
    if (login.error) login.reset();
  };
  const handlePasswordChange = (v: string) => {
    setPassword(v);
    if (login.error) login.reset();
  };

  return (
    <div
      className="min-h-screen relative bg-primary"
      data-testid="login-page"
    >
      {/*
        Phase 10 SPLASH-3.3 + V-M9: 3-layer brand carry on the first
        interactive web surface.
          Layer 1 (this outer div): bg-primary (forest_green token #406020)
            base ensures no white frame between OS splash dismiss and CSS
            load (V-M9 cold-paint invariant; same hex as Plan 10-02
            windowBackground on Android via the chrome_forest_green token).
            FIND-15-002 (2026-05-11): migrated from bg-[#406020] hardcoded
            hex to the bg-primary token utility class so chrome-component-audit
            and ESLint no-token-name-hardcoding rules pass cleanly.
          Layer 2 (absolute inset-0): watercolor master raster from Phase 8
            BRAND-1.2 — 149.3 KB JPEG self-served from /brand/ origin per
            CSP img-src 'self'.
          Layer 3 (absolute inset-0): forest_green wash overlay at 55%
            alpha on top of the watercolor; muted brand presence keeps the
            chrome card readable while the brand carry is visible.
      */}
      <div
        className="absolute inset-0 bg-[url('/brand/watercolor-master.jpg')] bg-cover bg-center"
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-primary/55" aria-hidden="true" />

      {/* Layer 4 (relative) — chrome content stacks above the wash.
          2026-05-11: top-aligned flex-col so the municipal logo + dept
          lines pin near the top of the viewport while the sign-in card
          stays vertically centered in the remaining space. The globe
          locale dropdown lives inside the card on the same row as the
          "Sign in" heading (opposite end via flex justify-between) —
          operator-directed 2026-05-11 to consolidate the auth chrome
          inside the card surface. */}
      <div className="relative min-h-screen flex flex-col items-center pbs-6">

        {/* 2026-05-11 — Main municipal logo + two-level org hierarchy
            pinned near the top. Operator calibration sequence:
              attempt 1: w-40 sm:w-48 + text-sm → too big
              attempt 2: w-28 sm:w-32 + text-xs → still too low + too big
              attempt 3 (this): w-24 sm:w-28 + text-[10px] at the top
                of the viewport. */}
        <div className="flex flex-col items-center mbe-4">
          <img
            src="/brand/unayzah-municipality-logo.png"
            alt=""
            aria-hidden="true"
            className="w-24 sm:w-28 h-auto mbe-1"
            data-testid="login-main-logo"
          />
          <p
            className="text-[10px] font-medium text-white text-center leading-tight"
            data-testid="login-org-parent"
          >
            <FormattedMessage id="app_org_parent_dept" />
          </p>
          <p
            className="text-[10px] font-medium text-white text-center leading-tight"
            data-testid="login-org-sub"
          >
            <FormattedMessage id="app_org_sub_dept" />
          </p>
        </div>

        {/* Centered card group fills the remaining vertical space. */}
        <div className="w-[400px] flex-1 flex flex-col justify-center pbe-8">

          {/* 2026-05-06 — official project name above the auth card so the
              municipal context is visible at sign-in. Bilingual via i18n;
              mirrors LTR/RTL automatically. text-white preserves WCAG-AA
              contrast on the wash overlay (white on #406020/55 over
              watercolor master ≈ 4.5:1).
              2026-05-11 — Latin (DIN Next W1G) at text-lg has heavier
              optical mass than DIN Next Arabic at the same px size; the
              EN headline read visibly larger than the AR. ltr:text-base
              shrinks EN to 16px while AR stays at text-lg (18px) so the
              two locales match visually. */}
          <h2
            className="ltr:text-base rtl:text-lg font-semibold text-white text-center mbe-3"
            data-testid="login-app-name-full"
          >
            <FormattedMessage id="app_name_full" />
          </h2>

        <div className="bg-surface-raised rounded-lg shadow-sm border border-divider p-8">
          {/* Clock-skew error above heading */}
          {isClockSkew && (
            <div className="mbe-4">
              <Banner variant="warning" data-testid="clock-skew-banner">
                {intl.formatMessage({ id: "ERR_CLIENT_CLOCK_SKEW" })}
              </Banner>
            </div>
          )}

          {/* "Sign in" heading + globe locale switcher on opposite ends of
              the same row. flex justify-between auto-flips under dir=rtl
              (the GlobeLocaleDropdown moves to the start edge in RTL,
              opposite the heading which is at the end). items-center
              vertically aligns the icon with the heading baseline. */}
          <div className="flex items-center justify-between mbe-6">
            <h1 className="text-2xl font-semibold text-on-surface">
              <FormattedMessage id="LABEL_SIGN_IN" />
            </h1>
            <GlobeLocaleDropdown />
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <TextField
              id="username"
              label={intl.formatMessage({ id: "LABEL_USERNAME" })}
              type="text"
              value={username}
              onChange={handleUsernameChange}
              autoComplete="username"
              required
              data-testid="username-input"
            />
            <div>
              <TextField
                id="password"
                label={intl.formatMessage({ id: "LABEL_PASSWORD" })}
                type="password"
                value={password}
                onChange={handlePasswordChange}
                autoComplete="current-password"
                required
                data-testid="password-input"
              />
              <div className="flex justify-end mbs-2">
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:text-primary-tint underline-offset-4 hover:underline transition-colors duration-200"
                  data-testid="forgot-link"
                >
                  <FormattedMessage id="LABEL_FORGOT_PASSWORD" />
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              fullWidth
              disabled={login.isPending}
              data-testid="sign-in-button"
            >
              <FormattedMessage id="LABEL_SIGN_IN" />
            </Button>

            {/* Generic auth-fail error inline below CTA */}
            {isAuthFailed && (
              <Banner variant="error" data-testid="auth-error-banner">
                {intl.formatMessage({ id: "ERR_AUTH_FAILED" })}
              </Banner>
            )}
            {/* 2026-05-02 walkthrough P2 — fallback banner for any other
                error code (network, ERR_VALIDATION, 5xx). Resolves to the
                catalog entry when present, otherwise falls back to a generic
                "something went wrong" copy. */}
            {isOtherError && (
              <Banner variant="error" data-testid="generic-error-banner">
                {intl.formatMessage({
                  id: errorCode!,
                  defaultMessage: intl.formatMessage({ id: "ERR_GENERIC" }),
                })}
              </Banner>
            )}
          </form>
        </div>

          {/* Publisher imprint below the auth card. text-white/70 keeps the
              visual softness while preserving WCAG-AA contrast on the wash
              overlay (the prior text-on-surface-variant from the bg-surface
              era resolves to a near-black that fails contrast against the
              forest-green wash; UI-SPEC line 222 caveat documented in
              10-03-SUMMARY.md axe-core verification). */}
          <p
            className="text-xs text-white/70 text-center mbs-4"
            data-testid="login-app-publisher"
          >
            <FormattedMessage id="app_publisher" />
          </p>
        </div>
      </div>
    </div>
  );
}
